import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Grid,
  Chip,
  alpha,
  useTheme
} from '@mui/material';
import { formatCurrency } from '../services/budgetService';

// Top Sheet - Executive Summary (like Movie Magic Budgeting)
function BudgetTopSheet({ budget, subcategories, departments = [], onAccountSelect }) {
  const theme = useTheme();
  
  if (!budget) return null;

  const { totals, globals, fringes, lineItems = [] } = budget;

  // Group line items by account for top sheet
  const getAccountSummary = () => {
    const summary = {};
    
    lineItems.forEach(item => {
      const subcatInfo = subcategories[item.subcategory];
      if (!subcatInfo) return;
      
      const account = subcatInfo.account;
      const label = subcatInfo.label;
      const category = subcatInfo.category;
      const subcategory = item.subcategory;
      const department = subcatInfo.department;
      
      if (!summary[account]) {
        summary[account] = {
          account,
          label,
          category,
          subcategory,
          department,
          budget: 0,
          actual: 0
        };
      }
      summary[account].budget += item.budgetAmount || 0;
      summary[account].actual += item.actualAmount || 0;
    });
    
    return Object.values(summary).sort((a, b) => a.account.localeCompare(b.account));
  };

  const accountSummary = getAccountSummary();
  
  // Group by category
  const atlAccounts = accountSummary.filter(a => a.category === 'ATL');
  const btlProdAccounts = accountSummary.filter(a => a.category === 'BTL_PRODUCTION');
  const btlPostAccounts = accountSummary.filter(a => a.category === 'BTL_POST');
  const otherAccounts = accountSummary.filter(a => a.category === 'OTHER');

  const CategorySection = ({ title, accounts, subtotal, fringeAmount, color }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ 
        bgcolor: alpha(color, 0.1), 
        p: 1.5, 
        borderRadius: 1,
        borderLeft: `4px solid ${color}`,
        mb: 1
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color }}>
          {title}
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '12%' }} />
            <col style={{ width: '44%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '16%' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 80 }}>Acct</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Budget</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actual</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Variance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((acct) => {
              const variance = acct.budget - acct.actual;
              const isClickable = Boolean(onAccountSelect);
              return (
                <TableRow
                  key={acct.account}
                  hover
                  onClick={() => {
                    if (isClickable) {
                      // Movie Magic style - pass account info for drill-down
                      onAccountSelect?.({
                        account: acct.account,
                        label: acct.label,
                        category: acct.category,
                        subcategory: acct.subcategory
                      });
                    }
                  }}
                  sx={{
                    cursor: isClickable ? 'pointer' : 'default'
                  }}
                >
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                    {acct.account}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'normal' }}>{acct.label}</TableCell>
                  <TableCell align="right">{formatCurrency(acct.budget)}</TableCell>
                  <TableCell align="right">{formatCurrency(acct.actual)}</TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      color: variance < 0 ? 'error.main' : 'success.main',
                      fontWeight: 500
                    }}
                  >
                    {formatCurrency(variance)}
                  </TableCell>
                </TableRow>
              );
            })}
            {fringeAmount > 0 && (
              <TableRow sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                  FRNG
                </TableCell>
                <TableCell sx={{ fontStyle: 'italic' }}>Fringes & Payroll Taxes</TableCell>
                <TableCell align="right">{formatCurrency(fringeAmount)}</TableCell>
                <TableCell align="right">-</TableCell>
                <TableCell align="right">-</TableCell>
              </TableRow>
            )}
            <TableRow sx={{ bgcolor: alpha(color, 0.05) }}>
              <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                SUBTOTAL - {title}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatCurrency(subtotal + (fringeAmount || 0))}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatCurrency(accounts.reduce((sum, a) => sum + a.actual, 0))}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatCurrency((subtotal + (fringeAmount || 0)) - accounts.reduce((sum, a) => sum + a.actual, 0))}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {budget.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Top Sheet Summary • Version {budget.version}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={globals?.productionType?.replace('-', ' ').toUpperCase() || 'FEATURE'} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
              <Chip 
                label={globals?.unionStatus?.toUpperCase() || 'NON-UNION'} 
                size="small" 
                variant="outlined"
              />
              <Chip 
                label={`${globals?.shootDays || 0} Shoot Days`} 
                size="small" 
                variant="outlined"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography variant="caption" color="text.secondary">GRAND TOTAL</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatCurrency(totals?.grandTotal || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contingency: {totals?.contingencyPercent || 10}% ({formatCurrency(totals?.contingencyAmount || 0)})
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Production Globals */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
          PRODUCTION GLOBALS
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Prep Days</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{globals?.prepDays || 0}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Shoot Days</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{globals?.shootDays || 0}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Wrap Days</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{globals?.wrapDays || 0}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">Total Days</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {(globals?.prepDays || 0) + (globals?.shootDays || 0) + (globals?.wrapDays || 0)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <CategorySection 
        title="ABOVE THE LINE" 
        accounts={atlAccounts}
        subtotal={totals?.atl || 0}
        fringeAmount={totals?.atlFringes || 0}
        color={theme.palette.info.main}
      />
      
      <CategorySection 
        title="BELOW THE LINE - PRODUCTION" 
        accounts={btlProdAccounts}
        subtotal={totals?.btlProduction || 0}
        fringeAmount={totals?.btlProductionFringes || 0}
        color={theme.palette.success.main}
      />
      
      <CategorySection 
        title="BELOW THE LINE - POST PRODUCTION" 
        accounts={btlPostAccounts}
        subtotal={totals?.btlPost || 0}
        fringeAmount={0}
        color={theme.palette.secondary.main}
      />
      
      {otherAccounts.length > 0 && (
        <CategorySection 
          title="OTHER COSTS" 
          accounts={otherAccounts}
          subtotal={totals?.other || 0}
          fringeAmount={0}
          color={theme.palette.warning.main}
        />
      )}

      {/* Grand Total Summary */}
      <Paper sx={{ 
        p: 3, 
        borderRadius: 1, 
        border: 1,
        borderColor: 'primary.main',
        bgcolor: alpha(theme.palette.primary.main, 0.02)
      }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 500 }}>Above The Line Total</TableCell>
                  <TableCell align="right" sx={{ border: 0 }}>
                    {formatCurrency((totals?.atl || 0) + (totals?.atlFringes || 0))}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 500 }}>Below The Line - Production</TableCell>
                  <TableCell align="right" sx={{ border: 0 }}>
                    {formatCurrency((totals?.btlProduction || 0) + (totals?.btlProductionFringes || 0))}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 500 }}>Below The Line - Post</TableCell>
                  <TableCell align="right" sx={{ border: 0 }}>
                    {formatCurrency(totals?.btlPost || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 500 }}>Other Costs</TableCell>
                  <TableCell align="right" sx={{ border: 0 }}>
                    {formatCurrency(totals?.other || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 500 }}>Total Fringes</TableCell>
                  <TableCell align="right" sx={{ border: 0 }}>
                    {formatCurrency(totals?.totalFringes || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={2} sx={{ border: 0, py: 0.5 }}>
                    <Box sx={{ borderTop: 1, borderColor: 'divider' }} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 600 }}>SUBTOTAL</TableCell>
                  <TableCell align="right" sx={{ border: 0, fontWeight: 600 }}>
                    {formatCurrency(totals?.subtotal || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 0, fontWeight: 500 }}>
                    Contingency ({totals?.contingencyPercent || 10}%)
                  </TableCell>
                  <TableCell align="right" sx={{ border: 0 }}>
                    {formatCurrency(totals?.contingencyAmount || 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              textAlign: 'center', 
              p: 2, 
              bgcolor: 'primary.main', 
              borderRadius: 1,
              color: 'white'
            }}>
              <Typography variant="overline">GRAND TOTAL</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatCurrency(totals?.grandTotal || 0)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Actual Spent</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formatCurrency(totals?.actualSpent || 0)}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: (totals?.variance || 0) >= 0 ? 'success.main' : 'error.main',
                  fontWeight: 600
                }}
              >
                {(totals?.variance || 0) >= 0 ? 'Under' : 'Over'} Budget: {formatCurrency(Math.abs(totals?.variance || 0))}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default BudgetTopSheet;
